import { Topic } from "@mui/icons-material";
import { Chip, CircularProgress, LinearProgress, Typography } from "@mui/material";
import { cloneDeep } from 'lodash';
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Api from "../../Api";
import { IDispatchMaterialGroup, IDispatchRequest, IImage, IItemValidationModel, IMaterial, IMaterialConsumeData } from "../../interfaces";
import { BreadcrumbBar } from "../Breadcrumb";
import { MaterialInformationForm } from "./MaterialInformation";
import { ExpandableContainer } from "../Shared/ExpandableContainer";
import FormNavigation from "../Shared/FormNavigation";
import { DispatchActions } from "./DispatchActions";
import { DispatchDetailForm } from "./DispatchDetail";
import "./index.scss";
import { MaterialFulfillmentForm } from "./MaterialGroupFulfillment";
import { MaterialGroupInformationForm } from "./MaterialGroupInformation";
import { Role, Values } from "../../utils";
import { PermissionContext } from "../../contexts/PermissionContext";


export const DispatchRequest: React.FC = ({
    ...props
}) => {

    const [dispatch, setDispatch] = useState<IDispatchRequest>();
    const [detailsValid, setDetailsValid] = useState<boolean>(false);
    const [materialGroups, setMaterialGroups] = useState<IDispatchMaterialGroup[]>([]);
    const [assignedMaterial, setAssignedMaterial] = useState<IItemValidationModel[]>([]);
    const [materials, setMaterials] = useState<IMaterial[]>([]);
    const [images, setImages] = useState<IImage[]>([]);
    const [bread, setBread] = useState<string>("");
    const [loadingActions, setLoadingActions] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);

    const [collectedBy, setCollectedBy] = useState("");

    const { drId } = useParams();

    const navigate = useNavigate();
    const permissionContext = useContext(PermissionContext);


    useEffect(() => {

        let requestedId: any = drId;
        if (requestedId) setBread("Update");
        else {
            setBread("Create");
            requestedId = null;
        }

        //when opening an existing request we will pass
        //in the id on the fetch request.

        fetchFormData(requestedId).then((data: any) => {
            populateForm(data);
        });

    }, []);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {

        validateDetails();



    }, [assignedMaterial, materialGroups, dispatch, detailsValid])

    const fetchFormData = async (id: string | null | undefined) => {

        if (id) {

            return Api.Dispatches.get(id);

        } else return Api.Dispatches.create().then((data) => {

            //update the url so page reloads dont cause
            //multiple requests to be created

            var dr = data as IDispatchRequest;
            window.history.pushState(
                "Update",
                "Update Dispatch",
                "/dispatchrequest/update/" + dr.dispatchRequestId);

            return dr;
        });

    }

    const populateForm = (data: IDispatchRequest) => {

        if (data) {

            console.log("DISPATCH", data);

            setDispatch(data);
            setAssignedMaterial(data.assignedMaterial);

            setMaterialGroups(data.materialGroups);
            setMaterials(data.materials);
            setImages(data.images);
            setCollectedBy(data.collectedBy ?? "");
        }

        setLoadingActions(false);
    }

    const saveDispatch = (updatedDispatch: IDispatchRequest) => {

        setSaving(true);
        setDispatch(updatedDispatch);

        Api.Dispatches.save(updatedDispatch).then((response) => {
            let updateResponse = response as IDispatchRequest;

            setDispatch(updateResponse);
            setAssignedMaterial(updateResponse.assignedMaterial);

            setSaving(false);
        });
    }

    const onConsume = (materialData: IMaterialConsumeData[], reason: string) => {

        setSaving(true);

        if (dispatch && dispatch.dispatchRequestId)
            Api.Dispatches.consume(dispatch.dispatchRequestId, materialData, reason).then((response) => {
                let updateResponse = response as IDispatchRequest;

                setDispatch(updateResponse);
                setMaterials(updateResponse.materials);

                setSaving(false);
            })

    }

    const onUpdateDispatchDetail = (dispatch: IDispatchRequest, extend: boolean = false) => {

        setDispatch(dispatch);

        if (extend === true) {
            Api.Dispatches.extend(dispatch).then(() => {
                validateDetails();
            });
        } else Api.Dispatches.save(dispatch).then(() => {
            validateDetails();
        });
    }

    const onUpdateGroups = (newGroups: IDispatchMaterialGroup[] | null | undefined) => {
        if (newGroups && dispatch) {
            let updatedDispatch: IDispatchRequest = cloneDeep(dispatch);
            updatedDispatch.materialGroups = newGroups;
            saveDispatch(updatedDispatch);

            setMaterialGroups(newGroups);
        }
    }

    const onUpdateGroup = async (group: IDispatchMaterialGroup | null | undefined, split?: IMaterial) => {

        let groups = cloneDeep(materialGroups);
        let newGroups: IDispatchMaterialGroup[] = [];

        groups.forEach(g => {
            if (g.id === group?.id) {
                newGroups.push(group)
            } else newGroups.push(g);
        });

        onUpdateGroups(newGroups);
    }

    const onUpdateAssigned = (updatedMaterials: IItemValidationModel[]) => {

        //if assignment is readonly we dont want to allow updates
        if (isReadonlyAssignable())
            return;

        if (assignedMaterial === updatedMaterials)
            return;

        setAssignedMaterial(updatedMaterials);

        if (dispatch) {
            let updatedDispatch: IDispatchRequest = cloneDeep(dispatch);
            updatedDispatch.assignedMaterial = updatedMaterials;

            saveDispatch(updatedDispatch);
        }
    }

    const validateDetails = () => {

        let form: HTMLFormElement = document.getElementById("dispatch-details-form") as HTMLFormElement;
        if (form && materialGroups && materialGroups.length > 0) {


            setDetailsValid(form.checkValidity());

            if (dispatch?.expectedReturnDate) {
                //every material must have an expected return condition
                materialGroups.forEach(materialGroup => {

                    if (!materialGroup.expectedReturnState) {
                        console.log("missing expectedReturnState", materialGroup);
                        setDetailsValid(prev => false);
                    }
                });
            }


        } else {

            console.log("missing validation items", form, materialGroups);
            setDetailsValid(prev => false);
        }

        //assigned material must all be fulfilled
        if (dispatch?.assignedMaterial && dispatch?.assignedMaterial.some(x => x.fulfilled === false)) {
            console.log("missing fulfillment", dispatch?.assignedMaterial.filter(x => x.fulfilled === false));
            setDetailsValid(prev => false);
        } else {
            console.log("all assigned", dispatch?.assignedMaterial)
        }

    }

    const onSubmit = async () => {
        setLoadingActions(true);
        return Api.Dispatches.submit(dispatch?.dispatchRequestId as string).then(async (r: any) => {
            fetchFormData(dispatch?.dispatchRequestId).then((data: any) => {
                setLoadingActions(false);
                navigate("/dispatchrequest");
            });
        });
    }

    const onDelete = async () => {
        setLoadingActions(true);
        return Api.Dispatches.delete(dispatch?.dispatchRequestId as string).then(async (r: any) => {
            navigate("/dispatchrequest");
        });
    }

    const onApprove = async (commercialUserId?: string, platformEngineerId?: string) => {
        setLoadingActions(true);
        return Api.Dispatches.approve(dispatch?.dispatchRequestId as string, commercialUserId, platformEngineerId).then(async (r) => {
            fetchFormData(dispatch?.dispatchRequestId).then((data: any) => {
                populateForm(data);
                setLoadingActions(false);
            });
        });
    }

    const onComplete = async () => {
        setLoadingActions(true);
        return Api.StorageRequests.complete(dispatch?.dispatchRequestId as string).then(async (r) => {
            fetchFormData(dispatch?.dispatchRequestId).then((data: any) => {
                populateForm(data);
                setLoadingActions(false);
            });
        });
    }

    const onReject = async (reason: string) => {
        setLoadingActions(true);
        return Api.Dispatches.reject(dispatch?.dispatchRequestId as string, reason).then(async (r) => {
            fetchFormData(dispatch?.dispatchRequestId).then((data: any) => {
                populateForm(data);
                setLoadingActions(false);
                navigate("/dispatchrequest");
            });
        });
    }

    const onReady = async () => {
        setLoadingActions(true);
        return Api.Dispatches.ready(dispatch?.dispatchRequestId as string).then(async (r) => {
            fetchFormData(dispatch?.dispatchRequestId).then((data: any) => {
                populateForm(data);
                setLoadingActions(false);
            });
        });
    }

    const onDispatched = async () => {
        setLoadingActions(true);
        return Api.Dispatches.dispatch(dispatch?.dispatchRequestId as string, collectedBy).then(async (r) => {
            fetchFormData(dispatch?.dispatchRequestId).then((data: any) => {
                populateForm(data);
                setLoadingActions(false);
            });
        });
    }

    const onClose = async (cancellationReason?: string) => {
        setLoadingActions(true);

        return Api.Dispatches.close(dispatch?.dispatchRequestId as string, cancellationReason).then(async (r) => {
            fetchFormData(dispatch?.dispatchRequestId).then((data: any) => {
                populateForm(data);
                setLoadingActions(false);
            });
        });
    }

    const getStatus = () => { return dispatch?.status ?? 0 }

    const getStatusLabel = () => {

        if (loadingActions)
            return <CircularProgress size="2rem" />;

        switch (getStatus()) {
            case 1:
                return <Chip
                    label={"Draft Dispatch"}
                    color="default"
                    variant="filled"
                />;
            case 2:
                return <Chip
                    label={"Awaiting Approval - Open"}
                    color="primary"
                    variant="outlined"
                />;
            case 3:
                return <Chip
                    label={"Awaiting Commercial Approval - Open"}
                    color="primary"
                    variant="outlined"
                />;
            case 4: return <Chip
                label={"Approved - Open"}
                color="primary"
                variant="outlined"
            />;
            case 5: return <Chip
                label={"Ready for Dispatch - Open"}
                color="primary"
                variant="outlined"
            />;
            case 6: return <Chip
                label={"Collected (Awaiting Return) - Open"}
                color="default"
                variant="outlined"
            />;
            case 7: return <Chip
                label={"Rejected - Open"}
                color="primary"
                variant="outlined"
            />;
            case 8: return <Chip
                label={"Rejected - Closed"}
                color="default"
                variant="filled"
            />;
            case 9: return <Chip
                label={"Approved - Closed"}
                color="default"
                variant="filled"
            />;
            case 10: return <Chip
                label={"Returned - Closed"}
                color="default"
                variant="filled"
            />;
            case 11: return <Chip
                label={"Cancelled - Closed"}
                color="default"
                variant="filled"
            />;
            default:
                return <Chip
                    label={"..."}
                    style={{ minWidth: '4rem' }}
                    color="primary"
                    variant="outlined"
                />;
        }

    }

    const isLoanTimeMandatory = () => {

        if (dispatch?.loanTimeMonths && dispatch?.loanTimeMonths > 0)
            return true;
        if (dispatch?.loanTimeYears && dispatch?.loanTimeYears > 0)
            return true;
        return false;
    }

    const isValueMandatory = () => {

        let id = dispatch?.commercialAgreement?.id?.toUpperCase() ?? "";

        if (["5D82640C-986E-42C6-97DF-6202D8FC58C5",
            "E65060F2-71FB-4563-A25B-FD14D5EBB2F7"].includes(id))
            return true;

        return false;
    }

    const isCVRequired = () => {

        let required = false

        if (materialGroups)
            materialGroups.forEach(m => {
                if (!Values.isStrategicSpareType(m.materialGroup.spareType)) {
                    required = true;
                }
            });

        return required;
    }

    const isImageUploadAllowed = () => {
        return [4, 5].includes(getStatus());
    }
    const isReadonly = () => {
        return [2, 3, 4, 5, 6, 8, 9, 10, 11].includes(getStatus());
    }
    const isAssignable = () => {
        return [2, 3, 4, 5].includes(getStatus());
    }
    const isReadonlyAssignable = () => {
        return [4, 5, 6, 8, 9].includes(getStatus());
    }
    const allowLoanTimeAdjustment = () => {
        return (getStatus() === 6 &&
            permissionContext.isRole(Role.Internal.AssetProtection))
    }

    useEffect(() => {
    }, [dispatch?.loanTimeMonths, dispatch?.loanTimeYears])

    return (
        <ExpandableContainer>
            <BreadcrumbBar
                current={bread}
                previous={[{
                    name: "Dispatch",
                    href: "/dispatchrequest"
                }]}
            />
            <Typography variant="h4" sx={{ py: 3 }}>{dispatch ? <>
                <Topic fontSize="inherit" sx={{ opacity: .8 }} color="primary" />
                {dispatch.reference}
                <FormNavigation />
            </> : <CircularProgress />} </Typography>
            {getStatusLabel()}

            {dispatch?.cancellationReason &&
                <Chip
                    sx={{ ml: 2, p: 0.5 }}
                    label={dispatch.cancellationReason}
                    color="warning"
                    variant="outlined"
                />
            }
            {dispatch &&
                <form id="dispatch-details-form">
                    <div className="form-section">
                        <DispatchDetailForm
                            dispatch={dispatch}
                            requireCVNumber={isCVRequired()}
                            updateDispatch={(dispatch) => onUpdateDispatchDetail(dispatch, allowLoanTimeAdjustment())}
                            readonly={isReadonly()}
                            allowLoanTimeAdjustment={allowLoanTimeAdjustment()}
                        />
                    </div>
                </form>
            }

            <div className="form-section">
                {isAssignable() ?
                    <MaterialFulfillmentForm
                        dispatchRequestId={dispatch?.dispatchRequestId ?? ""}
                        materialGroups={materialGroups}
                        assignedMaterial={assignedMaterial}
                        onUpdateMaterials={(materials) => { onUpdateAssigned(materials) }}
                        readonly={isReadonlyAssignable()}
                        saving={saving}
                    />
                    :
                    <MaterialGroupInformationForm
                        entityId={dispatch?.dispatchRequestId ?? undefined}
                        status={getStatus()}
                        materialGroups={materialGroups}
                        onUpdateGroups={onUpdateGroups}
                        onUpdateGroup={onUpdateGroup}
                        expectedReturn={isLoanTimeMandatory()}
                        valueOnDispatch={isValueMandatory()}
                        readonly={isReadonly()}
                        saving={saving}
                    />
                }
            </div>

            {materials.length > 0 &&
                <div className="form-section">
                    {saving ? <LinearProgress /> :
                        <MaterialInformationForm
                            entityId={dispatch?.dispatchRequestId ?? undefined}
                            entityReference={dispatch?.reference ?? undefined}
                            status={getStatus()}
                            materials={materials}
                            images={images}
                            onUpdateImages={(imgs) => setImages(imgs)}
                            onUpdateMaterial={() => { }}
                            onUpdateMaterials={() => { }}
                            onConsume={onConsume}
                            readonly={isReadonly()}
                            readonlyMaterialActions={!isImageUploadAllowed()}
                        />
                    }

                </div>
            }

            <div className="form-section">
                {loadingActions ? <LinearProgress /> :
                    <DispatchActions
                        status={getStatus()}
                        formValid={saving ? false : detailsValid}
                        approvers={[dispatch?.commercialApprovedBy, dispatch?.platformEngineerApprovedBy]}
                        commercialApprovedDate={dispatch?.dateCommercialApproved ?? undefined}
                        platformEngineerApprovedDate={dispatch?.datePlatformEngineerApproved ?? undefined}
                        {...{
                            onSubmit,
                            onDelete,
                            onApprove,
                            onReject,
                            onReady,
                            collectedBy,
                            setCollectedBy,
                            onDispatched: images.length > 0 ? onDispatched : undefined,
                            onClose,
                            onComplete
                        }}
                        readonly={false}
                    />
                }
            </div>

        </ExpandableContainer>
    );
}